import { useEffect, useRef, useState } from "react";
import api, { API, getSessionToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, SendHorizonal, BrainCircuit, FileText, PanelLeft } from "lucide-react";

const CONF_STYLES = {
  high: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-red-50 text-red-600 border-red-200",
};

function renderAnswer(text) {
  const clean = (text || "").replace(/\s*CONFIDENCE:?\s*(high|medium|low)?[^\n]*$/i, "");
  return clean.split("\n").map((line, i) => {
    const html = line
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\[(\d+)\]/g, '<sup class="inline-flex items-center justify-center text-[9px] font-bold text-[#0a2540] bg-slate-100 border border-slate-200 rounded px-1 mx-0.5">$1</sup>');
    if (/^\s*[-•*]\s+/.test(line)) {
      return <li key={i} className="ml-4 list-disc" dangerouslySetInnerHTML={{ __html: html.replace(/^\s*[-•*]\s+/, "") }} />;
    }
    if (/^#{1,3}\s/.test(line)) {
      return <div key={i} className="font-semibold mt-2" dangerouslySetInnerHTML={{ __html: html.replace(/^#{1,3}\s/, "") }} />;
    }
    if (!line.trim()) return <div key={i} className="h-2" />;
    return <p key={i} dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function SbChat() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [scope, setScope] = useState("all");
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showList, setShowList] = useState(false);
  const bottomRef = useRef(null);
  const skipFetchRef = useRef(null);

  useEffect(() => {
    api.get("/second-brain/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/second-brain/workspaces").then((r) => setWorkspaces(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    if (skipFetchRef.current === activeId) return;
    api.get(`/second-brain/chat/sessions/${activeId}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  }, [activeId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const newChat = () => { skipFetchRef.current = null; setActiveId(null); setMessages([]); setShowList(false); };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/second-brain/chat/sessions/${id}`);
    setSessions((s) => s.filter((x) => x.id !== id));
    if (id === activeId) newChat();
  };

  const send = async () => {
    const question = input.trim();
    if (!question || streaming) return;
    setInput("");
    setStreaming(true);

    let sid = activeId;
    try {
      if (!sid) {
        const r = await api.post("/second-brain/chat/sessions", {
          workspace_id: scope === "all" ? null : scope,
        });
        sid = r.data.id;
        skipFetchRef.current = sid;
        setActiveId(sid);
        setSessions((s) => [r.data, ...s]);
      }
      setMessages((m) => [...m, { id: `tmp-u`, role: "user", content: question },
        { id: "tmp-a", role: "assistant", content: "", sources: [], pending: true }]);

      const token = getSessionToken();
      const resp = await fetch(`${API}/second-brain/chat/sessions/${sid}/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ question }),
      });
      if (!resp.ok) throw new Error("Request failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "sources") {
            setMessages((m) => m.map((x) => (x.id === "tmp-a" ? { ...x, sources: ev.sources } : x)));
          } else if (ev.type === "delta") {
            acc += ev.text;
            const snapshot = acc;
            setMessages((m) => m.map((x) => (x.id === "tmp-a" ? { ...x, content: snapshot, pending: false } : x)));
          } else if (ev.type === "done") {
            setMessages((m) => m.map((x) => (x.id === "tmp-a"
              ? { ...x, id: `done-${Date.now()}`, content: ev.answer, sources: ev.sources || [], confidence: ev.confidence, answered: ev.answered, pending: false }
              : x)));
            setSessions((s) => s.map((x) => (x.id === sid && x.title === "New conversation" ? { ...x, title: question.slice(0, 80) } : x)));
          } else if (ev.type === "error") {
            toast.error(ev.message);
            setMessages((m) => m.filter((x) => x.id !== "tmp-a"));
          }
        }
      }
    } catch (e) {
      toast.error("Failed to get an answer. Please try again.");
      setMessages((m) => m.filter((x) => x.id !== "tmp-a"));
    } finally {
      setStreaming(false);
    }
  };

  const sessionList = (
    <div className="flex flex-col h-full">
      <div className="p-3">
        <Button onClick={newChat} className="w-full bg-[#0a2540] hover:bg-[#0a2540]/90 text-white text-xs h-9" data-testid="sb-new-chat-btn">
          <Plus size={14} className="mr-1.5" /> New conversation
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
        {sessions.map((s) => (
          <div
            key={s.id}
            onClick={() => { skipFetchRef.current = null; setActiveId(s.id); setShowList(false); }}
            data-testid={`sb-session-${s.id}`}
            className={`group flex items-center gap-2 px-2.5 py-2 rounded-sm text-xs cursor-pointer ${
              s.id === activeId ? "bg-slate-100 text-[#0a2540] font-medium" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <span className="flex-1 truncate">{s.title}</span>
            <button onClick={(e) => deleteSession(s.id, e)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500" data-testid={`sb-delete-session-${s.id}`}>
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        {sessions.length === 0 && <div className="px-3 py-4 text-[11px] text-slate-400 italic">No conversations yet</div>}
      </div>
    </div>
  );

  return (
    <div className="flex h-full" data-testid="sb-chat">
      <aside className={`${showList ? "flex" : "hidden"} md:flex w-60 shrink-0 border-r border-slate-200 bg-[#fafbfc] flex-col absolute md:static inset-y-0 z-20`}>
        {sessionList}
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 overflow-y-auto">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center px-6 text-center">
              <div className="h-14 w-14 rounded-2xl bg-[#0a2540] text-white flex items-center justify-center mb-4">
                <BrainCircuit size={26} />
              </div>
              <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>Ask your Second Brain</h2>
              <p className="text-sm text-slate-500 mt-1 max-w-md">
                Answers come only from your company's uploaded knowledge — with sources cited and a confidence level on every reply.
              </p>
              <div className="mt-5 flex items-center gap-2">
                <span className="gc-label">Scope</span>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger className="h-8 w-52 text-xs" data-testid="sb-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All knowledge</SelectItem>
                    {workspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
              {messages.map((m, i) => (
                <div key={m.id || i} data-testid={`sb-msg-${m.role}`}>
                  {m.role === "user" ? (
                    <div className="flex justify-end">
                      <div className="bg-[#0a2540] text-white text-sm rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[85%]">{m.content}</div>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      <div className="h-7 w-7 shrink-0 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-[#0a2540]">
                        <BrainCircuit size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        {m.pending ? (
                          <div className="flex gap-1 py-2">
                            {[0, 1, 2].map((d) => (
                              <span key={d} className="h-1.5 w-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: `${d * 150}ms` }} />
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-slate-800 space-y-1 leading-relaxed">{renderAnswer(m.content)}</div>
                        )}
                        {(m.confidence || (m.sources || []).length > 0) && !m.pending && (
                          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                            {m.confidence && (
                              <span className={`text-[10px] font-semibold uppercase tracking-wide border rounded-full px-2 py-0.5 ${CONF_STYLES[m.confidence] || CONF_STYLES.medium}`} data-testid="sb-confidence-badge">
                                {m.confidence} confidence
                              </span>
                            )}
                            {(m.sources || []).map((s) => (
                              <span key={s.n} className="inline-flex items-center gap-1 text-[10px] text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5" data-testid="sb-source-chip" title={s.folder ? `Folder: ${s.folder}` : ""}>
                                <FileText size={9} /> [{s.n}] {s.title.length > 34 ? s.title.slice(0, 34) + "…" : s.title}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white p-3">
          <div className="max-w-3xl mx-auto flex items-end gap-2">
            <button className="md:hidden p-2 text-slate-500" onClick={() => setShowList(!showList)} data-testid="sb-toggle-sessions">
              <PanelLeft size={16} />
            </button>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask anything about your company knowledge…"
              rows={1}
              className="resize-none min-h-[42px] max-h-32 text-sm"
              data-testid="sb-chat-input"
            />
            <Button onClick={send} disabled={streaming || !input.trim()} className="h-[42px] bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="sb-chat-send-btn">
              <SendHorizonal size={16} />
            </Button>
          </div>
          <div className="max-w-3xl mx-auto mt-1.5 text-[10px] text-slate-400 text-center">
            Answers only from uploaded knowledge · Sources cited · Original files never exposed
          </div>
        </div>
      </div>
    </div>
  );
}
